import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Grid, Typography, TextField,
} from '@material-ui/core';
import { Controller } from 'react-hook-form';
import GoogleMapsAutocomplete from 'shared/components/GoogleMapsAutocomplete';
import { Wrapper } from '@googlemaps/react-wrapper';
import { GMAP_API_KEY } from 'shared/constants/gmapConsts';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import { withStyles } from '@material-ui/core/styles';

const CustomTextField = withStyles(() => ({
  root: {
    '& .MuiFilledInput-root': {
      height: 45,
    },
  },
}))(TextField);

const MAX_FIELDS_NUMBER = 5;
const DEFAULT_LOCATION = { location: null, radius: '50' };

const LocationFilter = ({
  control, fields, append, remove, errors, trigger,
}) => {
  const [disabledRadius, setDisabledRadius] = useState(fields.map(f => parseInt(f.radius, 10) === 0));

  const handleRemoveField = index => {
    if (index) {
      remove(index);
      setDisabledRadius(disabledRadius.filter((v, i) => i !== index));
    }
  };

  const handleAppendField = () => {
    if (fields.length < MAX_FIELDS_NUMBER) {
      append(DEFAULT_LOCATION);
      setDisabledRadius([...disabledRadius, false]);
    }
  };

  return (
    <>
      {fields.map((item, index) => (
        <Grid container spacing={1} key={item.id}>
          <Grid item xs={12} sm={5}>
            <Wrapper apiKey={GMAP_API_KEY} version="weekly" libraries={['places']}>
              <Controller
                name={`locations.${index}.location`}
                control={control}
                defaultValue={item.location}
                render={({ field: { value, onChange } }) => {
                  const handleChange = newValue => {
                    onChange(newValue);
                    trigger();
                  };
                  return (
                    <GoogleMapsAutocomplete
                      index={index}
                      value={value}
                      onChange={handleChange}
                      setDisabledRadius={setDisabledRadius}
                      required
                    />
                  );
                }}
              />
            </Wrapper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name={`locations.${index}.radius`}
              control={control}
              defaultValue={item.radius}
              render={({ field: { value, onChange } }) => (
                <CustomTextField
                  fullWidth
                  type="number"
                  onKeyDown={e => {
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onInput={e => {
                    const v = e.target.value.replace(/[^\d.]/g, '');
                    e.target.value = v;
                  }}
                  id="enterSearchRadius"
                  variant="filled"
                  size="medium"
                  name="enterSearchRadius"
                  label="Search Radius, Mi"
                  value={value}
                  onChange={onChange}
                  disabled={disabledRadius[index]}
                  required
                  error={
                    !disabledRadius[index]
                    && fields[index].location
                    && Boolean(errors?.locations?.[index]?.radius)
                  }
                  helperText={
                    !disabledRadius[index]
                    && fields[index].location
                    && errors?.locations?.[index]?.radius?.message
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box display="flex">
              {(fields.length < MAX_FIELDS_NUMBER) && (
                <IconButton aria-label="add" onClick={handleAppendField} style={{ padding: 13 }}>
                  <AddIcon />
                </IconButton>
              )}
              {index > 0 && (
                <IconButton aria-label="delete" onClick={() => handleRemoveField(index)} style={{ padding: 13 }}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      ))}
      <Box mt={2} mb={0.5} display="flex" alignItems="center">
        <Box mr={1}>
          <InfoIcon color="secondary" />
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary">
            Please confirm ahead of time that your school will allow you
            to do a rotation in these states. Student is
            responsible to confirm this.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

LocationFilter.propTypes = {
  control: PropTypes.shape({}).isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  append: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    locations: PropTypes.arrayOf(PropTypes.shape({
      location: PropTypes.string,
      radius: PropTypes.string,
    })),
  }).isRequired,
  trigger: PropTypes.func.isRequired,
};

export default LocationFilter;
